import { React, useState, useEffect, useCallback}  from "react";
import "./CurrentlyReadingDetailsModal.css";
import axios from "axios";

import { API_BASE_URL } from "../../Variables";
import xIcon from "../../assets/images/x-icon.svg";
import checkMark from "../../assets/images/checkmark.svg";
import StarRating from '../StarRating'; 
import AddToShelf from "../AddToShelf";
import ProgressBar from '../ProgressBar';
import ConfirmationModal from '../ConfirmationModal';
import ReviewDetails from "../ReviewDetails";

function CurrentlyReadingDetailsModal( {setDisplayCurrentlyReadingModal, bookId, refreshShelves, switchToDetailsView} ) {
  const token = localStorage.getItem('token');
  
  const [book, setBook] = useState(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [isInShelf, setIsInShelf] = useState(false);
  const [progress, setProgress] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [readShelfId, setReadShelfId] = useState(null);

  const [openAddToShelf, setOpenAddToShelf] = useState(false);
  const [openConfirmFinish, setOpenConfirmFinish] = useState(false);

  /**
   * Check if the book is in any shelf
   */
  const checkBookInShelf = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/shelves/book/${bookId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.length > 0) {
        setIsInShelf(true);
      }
      else {
        setIsInShelf(false);
      }
    } catch (error) {
      setError('Failed to check if the book is in a shelf');
    }
  }, [bookId, token]);

  /**
   * Open the AddToShelf modal
   */
  const handleAddToShelf = () => {
    setOpenAddToShelf(true);
  };

  // Handle change in current page input
  const handlePageChange = (event) => {
    let value = parseInt(event.target.value, 10) || 0;

    if (value < 0) value = 0;
    if (value > book.pageCount) value = book.pageCount;

    setCurrentPage(value);
  };

  /**
   * Update progress when the user clicks the Update Progress button
   */
  const updateProgress = async () => {
    try {
      setError(null);
      await axios.put(`${API_BASE_URL}/progress/${bookId}`, {
        current_page: currentPage,
        status: 'In Progress',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProgress();
      refreshShelves();

      if(currentPage === book.pageCount){
        setOpenConfirmFinish(true);
      }

    } catch (error) {
      setError('Failed to update progress');
    }
  };

  /**
   * get the ID of the Read shelf
   * @returns - the ID of the Read shelf
   */
  const getReadShelfId = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/shelves/shelf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { shelfName: 'Read' }
      });
      setReadShelfId(response.data.id);
      return response.data.id;
    } catch (error) {
      setError('Failed to update progress');
    }
  };

  /**
   * Handle marking a book as finished
   * Updates the progress and moves the book to the Read shelf
   * @param {*} bookId - ID of the book
   */
  const handleMarkAsFinished = async (bookId) => {
    setError(null);
    let shelfId = readShelfId;

    if (!shelfId) {
      shelfId = await getReadShelfId();
    }

    try {
      await axios.put(`${API_BASE_URL}/progress/${bookId}`, {
        status: 'Finished',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchProgress();
    } catch (error) {
      setError('Failed to update progress');
    }
  
    try {
      await axios.post(`${API_BASE_URL}/books-in-shelf`, {
        book,
        book_id: bookId,
        shelf_ids: [shelfId],
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      
    } catch (error) {
      setError('Failed to save shelves');
    }
    finally {
      setOpenConfirmFinish(false);
      refreshShelves();
    }
  };


  const fetchProgress = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/progress/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProgress(response.data);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      setError('Failed to fetch progress');
    }
  }, [bookId, token]);

  /**
   * Fetch book details
   */
  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/books-api/${bookId}`);
        setBook(response.data.volumeInfo);
        setImage(response.data.volumeInfo.imageLinks && response.data.volumeInfo.imageLinks.thumbnail
        ? response.data.volumeInfo.imageLinks.thumbnail
        : 'https://via.placeholder.com/150');
      } catch (error) {
        setError(error);
      }
    };

    fetchBook();
    checkBookInShelf();
  }, [bookId, checkBookInShelf]);

  useEffect(() => {
    fetchProgress();
  }, [bookId, token, fetchProgress]);

  /**
   * Check if the book is in a shelf when the AddToShelf modal is closed
   */
  useEffect(() => {
    if (!openAddToShelf) {
      checkBookInShelf();
    }
  }, [openAddToShelf, checkBookInShelf]);
  
  return (
    <div className="book-details-overlay">
      {book ? (
        <div className="book-details-modal">
          <div className="modal-close-container">
            <img src={xIcon} alt="Close" onClick={() => setDisplayCurrentlyReadingModal(false)} className="modal-close"/>
          </div>

          {/* Book details */}
          <div className="book-details-col-1">
            <img src={image} alt={book.title} className="book-details-img"/>
            <div className="categories">
              {book.categories && book.categories.map((category, index) => (
                <p key={index} className="category-tag">{category}</p>
              ))}
            </div>
          </div>

          <div className="book-details-header">
            <h2 className="book-details-title">{book.title}</h2>
            <p>{book.authors}</p>
          </div>

          <div className="book-details-info">
            <div className="action-buttons">
              {book.averageRating !== undefined && <StarRating rating={book.averageRating} />}
              <button className="secondary-button details-buttons" onClick={() => switchToDetailsView()}>View details</button>
              <button className="primary-button details-buttons" onClick={() => handleAddToShelf()}> 
                {isInShelf && <img src={checkMark} alt="Checkmark" className="checkmark-icon"/>}
                Add to Shelf
              </button>
            </div>

            <div className="currently-reading-section">
              <div className="section-header progress-header">
                <h3 className="section-title">Progress</h3>
                <ProgressBar currentPage={progress.current_page} totalPages={book.pageCount} />
              </div>
              <div className="progress-actions">
                Current Page: 
                <div>
                  <input type="number" value={currentPage} className="page-input" onChange={handlePageChange} max={book.pageCount} min={0}/>
                  <span className="total-pages">/ {book.pageCount}</span>
                </div>
                <button className="secondary-button details-buttons" onClick={updateProgress}>Update Progress</button>
              </div>
            </div>

            <hr className="divider"/>

            <div className="currently-reading-section">
              <ReviewDetails bookId={bookId} />
            </div>
          </div>

          {/* Add to Shelf modal */}
          {openAddToShelf && <AddToShelf onClose={setOpenAddToShelf} bookId={bookId} book={book} refreshShelves={refreshShelves}/>}

          {openConfirmFinish && (
          <ConfirmationModal
            onClose={() => setOpenConfirmFinish(false)}
            onConfirm={() => handleMarkAsFinished(bookId)}
            title={`You have completed "${book.title}!"`}
            message={
              "Do you want to mark it as finished and move it to the Read shelf? \nYou can always change this later."
            }
          />
        )}

        </div>
      ) : (
        <div className="book-details-modal">
          <div className="modal-close-container">
            <img src={xIcon} alt="Close" onClick={() => setDisplayCurrentlyReadingModal(false)} className="modal-close"/>
          </div>
          {error ? (
            <div className="error">{error.message}. Please try again later. </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CurrentlyReadingDetailsModal;
